import type { ButtonProps } from "@braintrust/braintrust-ui-components";
import { PendingFreelancerActionButton } from "@js/apps/freelancer/components/pending-freelancer-action-button";
import { NotInterestedButton } from "@js/apps/jobs/components/not-interested-button";
import type { MainJobListingJob } from "@js/types/jobs";
import { assertUnreachable } from "@js/utils";

import {
  PendingBankingInfoButton,
  RespondToOfferButton,
  ReviewContractButton,
  ScheduleInterviewButton,
  StartAIInterviewButton,
  ViewApplicationButton,
  ViewJobButton,
} from "./components";

type JobActionButtonProps = {
  job: MainJobListingJob;
  bidId?: number;
  fullWidth?: boolean;
  size?: ButtonProps["size"];
  resultPosition?: number;
  searchResults?: boolean;
  statusForFreelancer?: EnumType<typeof ENUMS.JobFreelancerStatus>;
};

export const JobActionButton = ({
  job,
  bidId,
  statusForFreelancer,
  fullWidth,
  size,
  ...props
}: JobActionButtonProps) => {
  if (!statusForFreelancer) {
    return (
      <ViewJobButton
        jobId={job.id}
        fullWidth={fullWidth}
        size={size}
        {...props}
      />
    );
  }

  switch (statusForFreelancer) {
    case ENUMS.JobFreelancerStatus.ACCEPTING_APPLICATIONS:
      return (
        <ViewJobButton
          jobId={job.id}
          fullWidth={fullWidth}
          size={size}
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.INVITED_BY_CLIENT:
    case ENUMS.JobFreelancerStatus.INVITED_BY_MATCHER:
      return (
        <>
          <NotInterestedButton job={job} fullWidth={fullWidth} size={size} />
          <ViewJobButton
            jobId={job.id}
            fullWidth={fullWidth}
            size={size}
            {...props}
          />
        </>
      );
    case ENUMS.JobFreelancerStatus.PENDING_VERIFICATION:
    case ENUMS.JobFreelancerStatus.PENDING_APPROVAL:
      return (
        <>
          <ViewApplicationButton
            jobId={job.id}
            bidId={bidId}
            fullWidth={fullWidth}
            variant="secondary"
            size={size}
          />
          <PendingFreelancerActionButton
            fullWidth={fullWidth}
            size={size}
            variant="primary"
            withIcon
          />
        </>
      );
    case ENUMS.JobFreelancerStatus.APPLICATION_SENT:
    case ENUMS.JobFreelancerStatus.IN_REVIEW:
    case ENUMS.JobFreelancerStatus.IN_REVIEW_AFTER_AI_INTERVIEW:
      return (
        <ViewApplicationButton
          jobId={job.id}
          bidId={bidId}
          fullWidth={fullWidth}
          size={size}
          variant="primary"
          withIcon
        />
      );
    case ENUMS.JobFreelancerStatus.LIVE_INTERVIEWING:
      if (!job?.creator_user_id) return null;

      return (
        <ScheduleInterviewButton
          employerUserId={job.creator_user_id}
          fullWidth={fullWidth}
          size={size}
          variant="primary"
        />
      );
    case ENUMS.JobFreelancerStatus.AI_INTERVIEWING:
      if (!bidId) return null;

      return (
        <StartAIInterviewButton
          variant="primary"
          bidId={bidId}
          fullWidth={fullWidth}
          size={size}
        />
      );
    case ENUMS.JobFreelancerStatus.NEW_OFFER:
    case ENUMS.JobFreelancerStatus.UPDATED_OFFER:
      if (!job?.offer_id) return null;
      return (
        <RespondToOfferButton
          jobId={job.id}
          offerId={job.offer_id}
          fullWidth={fullWidth}
          size={size}
          variant="primary"
        />
      );
    case ENUMS.JobFreelancerStatus.OFFER_EXTENDED:
    case ENUMS.JobFreelancerStatus.OFFER_ACCEPTED:
      if (!job?.offer_id) return null;
      return (
        <ReviewContractButton
          jobId={job.id}
          offerId={job.offer_id}
          fullWidth={fullWidth}
          size={size}
          variant="primary"
        />
      );
    case ENUMS.JobFreelancerStatus.APPLICATION_REJECTED:
      return (
        <ViewApplicationButton
          jobId={job.id}
          bidId={bidId}
          fullWidth={fullWidth}
          size={size}
          variant="secondary"
        />
      );

    case ENUMS.JobFreelancerStatus.PENDING_BANK_INFORMATION:
      return (
        <PendingBankingInfoButton
          variant="primary"
          size={size}
          fullWidth={fullWidth}
        />
      );
    case ENUMS.JobFreelancerStatus.JOB_FILLED:
    case ENUMS.JobFreelancerStatus.JOB_CLOSED:
    case ENUMS.JobFreelancerStatus.JOB_ON_HOLD:
    case ENUMS.JobFreelancerStatus.NON_SUPPORTED_LOCATION:
      return null;

    default: {
      assertUnreachable(statusForFreelancer);
      return null;
    }
  }
};
